import React, { useCallback, useMemo } from 'react';

import { renderErrorMessage } from '@lyearn/form/src/Form/components/InputFields/renderers';
import { ErrorPosition } from '@lyearn/form/src/Form/types';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn, css } from '@lyearn/style';
import { Box, Select, Stack, Typography } from '@lyearn/ui';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { DayOfWeekEnum } from '@/types/schema';

import { DaysOfWeek } from '../../../conts';
import { FrequencyComponentProps } from '../../../types';

const maxWidth = css`
  max-width: 100%;
`;

const inputStyle = css`
  min-width: 0 !important;
  width: 0 !important;
  margin-left: 0;
  margin-right: 0;
`;

const CLASSES = {
  input: inputStyle,
};

export const getValue = (item: (typeof DaysOfWeek)[0], t: TFunction) => ({
  value: item.id,
  label: t(item.labelKey),
  shortLabel: t(item.shortLabelKey),
});

export const getOptions = (items: typeof DaysOfWeek, t: TFunction) =>
  items.map((item) => getValue(item, t));

const VALUE_COUNT = 3;

const Weekdays = ({
  value,
  onChange,
  disabled,
  className,
  fullWidth,
  error,
  size,
}: FrequencyComponentProps & { fullWidth?: boolean; error?: JSX.Element }) => {
  const { t } = useTranslation();
  const daysOfWeek = value.daysOfWeek ?? [DaysOfWeek[0].id];

  const onValueChange = useCallback(
    (value: DayOfWeekEnum[]) => onChange({ daysOfWeek: value }),
    [onChange],
  );

  const options = useMemo(() => getOptions(DaysOfWeek, t), [t]);

  const renderTags = React.useCallback(
    (values: typeof options) => {
      const typographyVariant = size === 'medium' ? 'body-short-02' : 'body-short-01';

      if (values?.length === 1) {
        return (
          <Typography color="text-text-paragraph" numberOfLines={1} variant={typographyVariant}>
            {values[0].label}
          </Typography>
        );
      }

      const labels = values?.reduce((acc, it, index) => {
        if (!fullWidth && index > VALUE_COUNT - 1) return acc;
        if (it) acc.push(it.shortLabel);

        return acc;
      }, [] as string[]);

      return (
        <Box className={cn(maxWidth, 'flex items-center overflow-hidden')}>
          <Typography
            className="inline-block"
            color="text-text-paragraph"
            numberOfLines={1}
            variant={typographyVariant}>
            {labels?.join(', ')}
          </Typography>
          {fullWidth || values?.length <= VALUE_COUNT ? null : (
            <Typography
              className="inline-block ps-4 pe-8"
              color="text-text-secondary"
              variant={typographyVariant}>
              {`+${values?.length - VALUE_COUNT}`}
            </Typography>
          )}
        </Box>
      );
    },
    [fullWidth, size],
  );

  return (
    <Stack className={cn(className, fullWidth ? 'w-full' : 'w-1/2')} direction="col" gapY={8}>
      <Select
        disableClearable
        multiple
        className="w-full"
        classes={daysOfWeek.length ? CLASSES : undefined}
        disabled={disabled}
        error={Boolean(error)}
        getOptionLabel={getSelectOptionLabel}
        options={options}
        placeholder={t('Common:Weekdays.Placeholder')}
        renderTags={renderTags}
        size={size ?? 'large'}
        value={daysOfWeek}
        onChange={onValueChange}
      />
      {error
        ? renderErrorMessage({ errorMessage: error, errorPosition: ErrorPosition.Custom })
        : null}
    </Stack>
  );
};

export default Weekdays;
